import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Search, Plus, MoreHorizontal, Power, History, MapPin, Building2, Star, Eye, UserRound, ShieldAlert, Wand2, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { SearchableSelect } from '@/components/common/Combobox';
import { canManageAssignments, canReactivateAssignments, isOwner } from '@/lib/workspaces';
import { useTableStandard, SortTh, FilterTh, TableToolbar } from '@/lib/tableStandard';
import { OwnerPurgeDialog } from '@/components/admin/OwnerPurgeDialog';

const STATUS_TONE = {
  current: 'bg-[#8FC93A]/15 text-[#4e7a12] border-[#8FC93A]/40',
  future: 'bg-blue-50 text-blue-700 border-blue-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
  inactive: 'bg-red-50 text-red-600 border-red-200',
};
// User-facing status labels (Section 5). 'current' reads as "Active Now".
const STATUS_LABEL = { current: 'Active Now', future: 'Future', expired: 'Expired', inactive: 'Inactive' };

// Small personnel thumbnail — reuses the AUTHORITATIVE Personnel Master headshot
// (same source as Personnel Master) via the cached ?variant=thumb endpoint.
const PersonThumb = ({ personnelId, hasPhoto, token, testid }) => {
  const [failed, setFailed] = useState(false);
  const show = personnelId && hasPhoto && !failed;
  return (
    <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center" data-testid={testid}>
      {show ? (
        <img src={`${API}/personnel/${personnelId}/photo?variant=thumb&auth=${token}`} alt=""
          loading="lazy" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : <UserRound size={18} className="text-slate-300" />}
    </span>
  );
};

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
  </label>
);
const inputCls = 'w-full rounded-md border border-gray-200 px-3 py-2 text-sm';
const filterCls = 'rounded-md border border-gray-200 px-3 py-2 text-sm min-w-[140px]';

export const AssignmentManager = ({ personnelId = null, embedded = false }) => {
  const { token, user } = useContext(AuthContext);
  const loadSeq = useRef(0);
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const canManage = canManageAssignments(user);
  const canReactivate = canReactivateAssignments(user);
  const owner = isOwner(user);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ assignment_roles: [], assignment_scopes: [] });
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('all');
  const [fScope, setFScope] = useState('');
  const [fRole, setFRole] = useState('');
  const [fCustomer, setFCustomer] = useState('');
  const [fLocation, setFLocation] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [statusTarget, setStatusTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [compTarget, setCompTarget] = useState(null);
  const [compReqOpen, setCompReqOpen] = useState(false);
  const [compBusy, setCompBusy] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const loadQueueCount = useCallback(() => {
    axios.get(`${API}/assignments/compensation-requests/queue`, H)
      .then((r) => setQueueCount(r.data?.count || 0)).catch(() => {});
  }, [token]);
  const decideComp = async (reqId, decision) => {
    if (compBusy) return;
    setCompBusy(true);
    try {
      await axios.post(`${API}/assignments/compensation-request/${reqId}/decision`, { decision },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Compensation request ${decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'returned'}`);
      setCompTarget(null); setCompReqOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Decision failed'); }
    setCompBusy(false);
  };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (personnelId) params.set('personnel_id', personnelId);
    if (fCustomer) params.set('customer_id', fCustomer);
    if (fLocation) params.set('location_id', fLocation);
    if (fScope) params.set('assignment_scope', fScope);
    if (fRole) params.set('assignment_role', fRole);
    if (fStatus) params.set('status_filter', fStatus);
    if (search) params.set('search', search);
    const reqId = (loadSeq.current += 1);
    try {
      const r = await axios.get(`${API}/assignments?${params.toString()}`, H);
      if (reqId === loadSeq.current) setRows(r.data || []);  // ignore stale out-of-order responses
    } catch (e) { if (reqId === loadSeq.current) toast.error('Failed to load assignments'); }
  }, [personnelId, fCustomer, fLocation, fScope, fRole, fStatus, search, token]);

  useEffect(() => {
    axios.get(`${API}/assignments/meta`, H).then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/customers`, H).then((r) => setCustomers((r.data || []).filter((c) => (c.status || 'active') === 'active'))).catch(() => {});
    axios.get(`${API}/service-locations`, H).then((r) => setLocations((r.data || []).filter((l) => (l.status || 'active') === 'active'))).catch(() => {});
    axios.get(`${API}/personnel?status=active`, H).then((r) => setPersonnel(r.data || [])).catch(() => {});
    loadQueueCount();
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const std = useTableStandard(rows, { defaultSort: { key: null, dir: 0 } });
  // Section 4: show the Scope column ONLY when a non-location (customer-wide) scope
  // is actually present. A null-scope legacy row does not count as a second type.
  const showScope = rows.some((r) => r.assignment_scope === 'customer');

  return (
    <div data-testid={embedded ? 'assignments-embedded' : 'assignments-manager'}>
      {!embedded && (
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-[#0E2A47]">Personnel Assignments</h2>
            <p className="text-sm text-slate-500 mt-1">Effective-dated links between Personnel and customers / service locations.</p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {canManage && (
              <button onClick={() => setEditing(null)} data-testid="add-assignment-btn"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] sm:flex-none">
                <Plus size={16} /> New Assignment
              </button>
            )}
            <button onClick={() => setQueueOpen(true)} data-testid="comp-queue-open"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-[#0E2A47] px-4 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5 sm:flex-none">
              <DollarSign size={16} /> Compensation Approvals
              {queueCount > 0 && <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white" data-testid="comp-queue-badge">{queueCount}</span>}
            </button>
          </div>
        </div>
      )}

      {queueOpen && (
        <CompApprovalQueue token={token} owner={owner}
          onClose={() => setQueueOpen(false)}
          onDecided={() => { loadQueueCount(); load(); }} />
      )}

      {embedded && canManage && (
        <div className="mb-3 flex justify-end">
          <button onClick={() => setEditing(null)} data-testid="add-assignment-btn-embedded"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#16324F]">
            <Plus size={14} /> Add Assignment
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or personnel #"
            data-testid="assignment-search" className="w-full rounded-md border border-gray-200 pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="assignment-filter-status" className={filterCls}>
          <option value="all">All Statuses</option>
          <option value="current">Active Now</option>
          <option value="future">Future</option>
          <option value="expired">Expired</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={fScope} onChange={(e) => setFScope(e.target.value)} data-testid="assignment-filter-scope" className={filterCls}>
          <option value="">All scopes</option>
          <option value="customer">Customer-level</option>
          <option value="service_location">Location-level</option>
        </select>
        <select value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="assignment-filter-role" className={filterCls}>
          <option value="">All roles</option>
          {meta.assignment_roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {!personnelId && (
          <select value={fCustomer} onChange={(e) => { setFCustomer(e.target.value); setFLocation(''); }} data-testid="assignment-filter-customer" className={filterCls}>
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.company_name}</option>)}
          </select>
        )}
        {!personnelId && (
          <select value={fLocation} onChange={(e) => setFLocation(e.target.value)} data-testid="assignment-filter-location" className={filterCls}>
            <option value="">All service locations</option>
            {locations
              .filter((l) => !fCustomer || l.customer_id === fCustomer)
              .map((l) => <option key={l.id} value={l.id}>{l.location_name || l.name}</option>)}
          </select>
        )}
      </div>

      <TableToolbar std={std} total={rows.length} noun="assignment" idPrefix="assignments" />

      <div className="overflow-auto rounded-lg border border-gray-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortTh label="Personnel" k="name" std={std} testid="assignments-sort-name" />
              <SortTh label="Personnel #" k="employee_identifier" std={std} testid="assignments-sort-number" />
              {showScope && <SortTh label="Scope" k="assignment_scope" std={std} testid="assignments-sort-scope" />}
              <SortTh label="Customer" k="customer_name" std={std} testid="assignments-sort-customer" />
              <SortTh label="Service Location" k="location_name" std={std} testid="assignments-sort-location" />
              <SortTh label="Effective" k="effective_start_date" std={std} testid="assignments-sort-effective" />
              <SortTh label="Status" k="effective_status" std={std} testid="assignments-sort-status" />
              <th className="px-3 py-2">Compensation</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
            <tr>
              <FilterTh std={std} field="name" placeholder="Filter name" testid="assignments-filter-name" />
              <FilterTh std={std} field="employee_identifier" placeholder="Filter #" testid="assignments-colfilter-number" />
              {showScope && <FilterTh std={std} field="assignment_scope" placeholder="Filter" testid="assignments-colfilter-scope" />}
              <FilterTh std={std} field="customer_name" placeholder="Filter customer" testid="assignments-colfilter-customer" />
              <FilterTh std={std} field="location_name" placeholder="Filter location" testid="assignments-colfilter-location" />
              <th className="px-2 pb-2" />
              <FilterTh std={std} field="effective_status" placeholder="Filter" testid="assignments-colfilter-status" />
              <th className="px-2 pb-2" />
              <th className="px-2 pb-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {std.matchCount === 0 && (
              <tr><td colSpan={showScope ? 9 : 8} className="px-3 py-8 text-center text-slate-400" data-testid="assignments-empty">No assignments found.</td></tr>
            )}
            {std.view.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50/60" data-testid={`assignment-row-${a.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <PersonThumb personnelId={a.personnel_id} hasPhoto={a.personnel_has_photo} token={token} testid={`assignment-thumb-${a.id}`} />
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-1.5">
                        {a.is_primary && <Star size={13} className="text-amber-500 fill-amber-400" />}
                        {a.name}
                        {a.is_legacy_unlinked && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`assignment-legacy-${a.id}`}>UNLINKED LEGACY</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-500">{a.employee_identifier ? `#${a.employee_identifier}` : '—'}</td>
                {showScope && (
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      {a.assignment_scope === 'customer' ? <Building2 size={13} /> : a.assignment_scope === 'service_location' ? <MapPin size={13} /> : null}
                      {a.assignment_scope === 'customer' ? 'Customer' : a.assignment_scope === 'service_location' ? 'Location' : '—'}
                    </span>
                  </td>
                )}
                <td className="px-3 py-2 text-slate-600">
                  {a.account_number && <div className="text-xs font-semibold text-[#0E2A47]" data-testid={`assignment-account-${a.id}`}>{a.account_number} — {a.account_name || '—'}</div>}
                  <div>{a.customer_name || '—'}</div>
                </td>
                <td className="px-3 py-2 text-slate-600" data-testid={`assignment-location-${a.id}`}>{a.location_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-500" data-testid={`assignment-effective-${a.id}`}>{a.effective_start_date ? fmtDate(a.effective_start_date) : '—'}{a.effective_end_date ? ` → ${fmtDate(a.effective_end_date)}` : ' → —'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[a.effective_status] || 'bg-slate-100'}`} data-testid={`assignment-status-${a.id}`}>{STATUS_LABEL[a.effective_status] || a.effective_status}</span>
                </td>
                <td className="px-3 py-2" data-testid={`assignment-comp-${a.id}`}>
                  {a.compensation ? (
                    <button onClick={() => setCompTarget(a)} className="text-left hover:underline" data-testid={`assignment-comp-open-${a.id}`}>
                      <div className="text-xs font-semibold text-[#0E2A47]">{a.compensation.frequency}{a.compensation.amount != null ? ` · $${Number(a.compensation.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</div>
                      <div className="text-[10px] text-slate-400">{a.compensation.rate_basis_label}</div>
                    </button>
                  ) : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`assignment-nocomp-${a.id}`}>No approved pay</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger data-testid={`assignment-menu-${a.id}`}><MoreHorizontal size={16} className="text-slate-500" /></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setHistoryTarget(a)} data-testid={`assignment-history-${a.id}`}>
                        <History size={14} className="mr-2" /> View History
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCompTarget(a)} data-testid={`assignment-comp-menu-${a.id}`}>
                        <DollarSign size={14} className="mr-2" /> View Compensation
                      </DropdownMenuItem>
                      {canManage && !a.is_legacy_unlinked && (
                        <DropdownMenuItem onClick={() => setEditing(a)} data-testid={`assignment-edit-${a.id}`}>Edit Assignment</DropdownMenuItem>
                      )}
                      {canManage && !a.is_legacy_unlinked && a.is_active && (
                        <DropdownMenuItem onClick={() => setStatusTarget({ a, next: false })} data-testid={`assignment-end-${a.id}`}>
                          <Power size={14} className="mr-2" /> End / Deactivate
                        </DropdownMenuItem>
                      )}
                      {canReactivate && !a.is_legacy_unlinked && !a.is_active && (
                        <DropdownMenuItem onClick={() => setStatusTarget({ a, next: true })} data-testid={`assignment-reactivate-${a.id}`}>
                          <Power size={14} className="mr-2" /> Reactivate
                        </DropdownMenuItem>
                      )}
                      {a.is_legacy_unlinked && owner && (
                        <DropdownMenuItem onClick={() => setReconcileTarget(a)} data-testid={`assignment-reconcile-${a.id}`}>
                          <Wand2 size={14} className="mr-2" /> Reconcile Legacy Record
                        </DropdownMenuItem>
                      )}
                      {a.is_legacy_unlinked && !owner && (
                        <DropdownMenuItem disabled data-testid={`assignment-legacy-readonly-${a.id}`}>
                          <Eye size={14} className="mr-2" /> Unlinked legacy — Owner must reconcile
                        </DropdownMenuItem>
                      )}
                      {!canManage && !a.is_legacy_unlinked && (
                        <DropdownMenuItem disabled data-testid={`assignment-readonly-${a.id}`}>
                          <Eye size={14} className="mr-2" /> Read-only — manage access required
                        </DropdownMenuItem>
                      )}
                      {owner && (
                        <DropdownMenuItem onClick={() => setPurgeTarget(a)} data-testid={`assignment-purge-${a.id}`}
                          className="text-red-600 focus:text-red-700">
                          <ShieldAlert size={14} className="mr-2" /> Purge (Owner only)
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== undefined && (
        <AssignmentForm assignment={editing} lockedPersonnelId={personnelId} meta={meta}
          customers={customers} personnel={personnel} token={token}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
      {statusTarget && (
        <StatusDialog target={statusTarget} token={token}
          onClose={() => setStatusTarget(null)} onSaved={() => { setStatusTarget(null); load(); }} />
      )}
      {reconcileTarget && (
        <ReconcileDialog assignment={reconcileTarget} token={token}
          onClose={() => setReconcileTarget(null)} onSaved={() => { setReconcileTarget(null); load(); }} />
      )}
      {purgeTarget && (
        <OwnerPurgeDialog entityType="assignment" entityId={purgeTarget.id}
          onClose={() => setPurgeTarget(null)} onPurged={load} />
      )}
      {historyTarget && (
        <Dialog open onOpenChange={historyTarget ? () => setHistoryTarget(null) : undefined}>
          <DialogContent><DialogHeader><DialogTitle>Assignment History — {historyTarget.name}</DialogTitle></DialogHeader>
            <div className="max-h-[50vh] space-y-2 overflow-auto">
              {(historyTarget.status_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="rounded-md border border-gray-100 p-2.5 text-xs">
                  <div className="font-semibold capitalize text-slate-700">{h.action} {h.reason ? `— ${h.reason}` : ''}</div>
                  <div className="text-slate-400">{h.at ? fmtDateTime(h.at) : ''}{h.by_name ? ` · ${h.by_name}` : ''}</div>
                </div>
              ))}
              {(historyTarget.status_history || []).length === 0 && <div className="text-sm text-slate-400">No history.</div>}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {compTarget && (
        <Dialog open onOpenChange={() => { setCompTarget(null); setCompReqOpen(false); }}>
          <DialogContent data-testid="assignment-comp-modal">
            <DialogHeader><DialogTitle>Compensation — {compTarget.name}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-[11px] text-slate-500">Authoritative source: <b>Total Pay</b> compensation (single source of truth). Approved changes are recorded there and reflected here — no competing values.</p>
              {compTarget.compensation ? (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="assignment-comp-current">
                  <div className="font-semibold text-[#0E2A47]">{compTarget.compensation.frequency} · {compTarget.compensation.amount != null ? `$${Number(compTarget.compensation.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</div>
                  <div className="text-xs text-slate-500">{compTarget.compensation.rate_basis_label} · Effective {compTarget.compensation.effective_start_date || '—'}{compTarget.compensation.effective_end_date ? ` → ${compTarget.compensation.effective_end_date}` : ''}</div>
                  <div className="mt-1 text-[11px] text-slate-400">{compTarget.compensation.approval_status} · Approved by {compTarget.compensation.approved_by || '—'}</div>
                </div>
              ) : <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="assignment-comp-none">No approved compensation recorded for this assignment in Total Pay.</div>}

              {compTarget.pending_compensation_request && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="assignment-comp-pending">
                  <div className="text-xs font-semibold text-amber-800">Pending approval — {compTarget.pending_compensation_request.change_type}</div>
                  <div className="text-[11px] text-amber-700">
                    Proposed {compTarget.pending_compensation_request.proposed?.frequency_label || ''} ·
                    {' '}{(() => { const p = compTarget.pending_compensation_request.proposed || {}; const amt = p.approved_monthly_payout ?? p.weekly_reference_amount ?? p.rate; return amt != null ? `$${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'; })()}
                    {' '}· Effective {compTarget.pending_compensation_request.requested_effective_date} · by {compTarget.pending_compensation_request.initiated_by}
                  </div>
                  <div className="mt-1 text-[11px] text-amber-600">Reason: {compTarget.pending_compensation_request.reason}</div>
                  {owner && (
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => decideComp(compTarget.pending_compensation_request.id, 'approve')} disabled={compBusy} data-testid="assignment-comp-approve" className="rounded bg-[#5FA718] px-3 py-1 text-xs font-semibold text-white hover:bg-[#4e8f12] disabled:opacity-60">Approve</button>
                      <button onClick={() => decideComp(compTarget.pending_compensation_request.id, 'reject')} disabled={compBusy} data-testid="assignment-comp-reject" className="rounded bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60">Reject</button>
                      <button onClick={() => decideComp(compTarget.pending_compensation_request.id, 'return')} disabled={compBusy} data-testid="assignment-comp-return" className="rounded border border-amber-400 px-3 py-1 text-xs font-semibold text-amber-700">Return</button>
                    </div>)}
                  {!owner && <div className="mt-1 text-[10px] text-amber-600">Awaiting Global Administrator approval.</div>}
                </div>)}

              {canManage && !compTarget.pending_compensation_request && (
                compReqOpen
                  ? <CompRequestForm assignment={compTarget} token={token}
                      onCancel={() => setCompReqOpen(false)}
                      onDone={() => { setCompReqOpen(false); setCompTarget(null); load(); }} />
                  : <button onClick={() => setCompReqOpen(true)} data-testid="assignment-comp-request-open"
                      className="rounded-lg border border-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                      {compTarget.compensation ? 'Request Compensation Change' : 'Establish Initial Compensation'}
                    </button>
              )}
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">Compensation change history</div>
                <div className="max-h-[40vh] space-y-1.5 overflow-auto">
                  {(compTarget.compensation_history || []).map((h, i) => (
                    <div key={i} className="rounded border border-gray-100 p-2 text-xs">
                      <div className="font-medium">{h.frequency} · {h.amount != null ? `$${Number(h.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'} <span className="text-slate-400">({h.rate_basis_label})</span></div>
                      <div className="text-slate-400">Eff {h.effective_start_date || '—'}{h.effective_end_date ? ` → ${h.effective_end_date}` : ''} · {h.status_label || (h.is_active ? 'Active' : 'Superseded')}{h.approved_by ? ` · ${h.approved_by}` : ''}</div>
                    </div>))}
                  {(compTarget.compensation_history || []).length === 0 && <div className="text-xs text-slate-400">No compensation history.</div>}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const AssignmentForm = ({ assignment, lockedPersonnelId, meta, customers, personnel, token, onClose, onSaved }) => {
  const isEdit = !!assignment;
  const [f, setF] = useState({
    personnel_id: assignment?.personnel_id || lockedPersonnelId || '',
    assignment_scope: assignment?.assignment_scope || 'service_location',
    customer_id: assignment?.customer_id || '',
    location_id: assignment?.location_id || '',
    account_number: assignment?.account_number || '',
    assignment_role: assignment?.assignment_role || '',
    is_primary: assignment?.is_primary || false,
    effective_start_date: assignment?.effective_start_date || new Date().toISOString().slice(0, 10),
    effective_end_date: assignment?.effective_end_date || '',
    assignment_notes: assignment?.assignment_notes || '',
  });
  const [locations, setLocations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reactivate, setReactivate] = useState(null); // {personnel_id, personnel_name}
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!f.customer_id) { setLocations([]); return; }
    axios.get(`${API}/service-locations/by-customer/${f.customer_id}`, H)
      .then((r) => setLocations((r.data || []).filter((l) => l.is_active !== false && (l.status || 'active') === 'active')))
      .catch(() => setLocations([]));
  }, [f.customer_id]);

  useEffect(() => {
    if (!f.customer_id) { setAccounts([]); return; }
    axios.get(`${API}/assignments/customer-accounts?customer_id=${f.customer_id}`, H)
      .then((r) => {
        const a = r.data || [];
        setAccounts(a);
        // Only auto-pick a default account for CUSTOMER-wide scope. For a
        // location-scoped assignment the account is derived from the location.
        if (f.assignment_scope === 'customer') {
          setF((p) => (p.account_number ? p : { ...p, account_number: a[0]?.account_number || '' }));
        }
      })
      .catch(() => setAccounts([]));
  }, [f.customer_id]);

  useEffect(() => {
    if (!f.personnel_id || !f.customer_id) { setWarnings([]); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/assignments/check`, { ...f, exclude_id: assignment?.id }, H)
        .then((r) => setWarnings(r.data?.warnings || [])).catch(() => setWarnings([]));
    }, 300);
    return () => clearTimeout(t);
  }, [f.personnel_id, f.customer_id, f.location_id, f.assignment_scope, f.assignment_role, f.effective_start_date, f.effective_end_date]);

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/assignments/${assignment.id}`, {
          assignment_role: f.assignment_role, is_primary: f.is_primary,
          account_number: f.account_number,
          effective_start_date: f.effective_start_date, effective_end_date: f.effective_end_date || null,
          assignment_notes: f.assignment_notes,
        }, H);
      } else {
        await axios.post(`${API}/assignments`, { ...f, effective_end_date: f.effective_end_date || null }, H);
      }
      toast.success(`Assignment ${isEdit ? 'updated' : 'created'}`);
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Inactive person → offer Reactivate & Continue WITHOUT losing entered data.
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'personnel_inactive') {
        setReactivateReason('');
        setReactivate({ personnel_id: detail.personnel_id || f.personnel_id, personnel_name: detail.personnel_name || personName });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save assignment');
      }
    } finally { setBusy(false); }
  };

  const doReactivateAndContinue = async () => {
    if (reactivateReason.trim().length < 3) { toast.error('A reactivation reason is required'); return; }
    setReactivating(true);
    try {
      await axios.patch(`${API}/personnel/${reactivate.personnel_id}/status`,
        { status: 'active', reason: reactivateReason.trim() }, H);
      toast.success(`${reactivate.personnel_name || 'Personnel'} reactivated`);
      setReactivate(null);
      await save(); // retry with all entered fields preserved
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('You do not have authority to reactivate personnel. Ask a Global Administrator to reactivate, then reopen this assignment. Your entries are preserved.');
      } else {
        toast.error(e.response?.data?.detail || 'Reactivation failed');
      }
    } finally { setReactivating(false); }
  };

  const personnelOpts = personnel.map((p) => ({ value: p.id, label: `${p.name} — #${p.personnel_number}`, sublabel: p.worker_classification }));
  const personLocked = isEdit || !!lockedPersonnelId;
  const lockedPerson = personnel.find((p) => p.id === f.personnel_id);
  const personName = lockedPerson?.name || assignment?.name || '';
  const personNumber = lockedPerson?.personnel_number || assignment?.employee_identifier || '';
  const personClass = lockedPerson?.worker_classification || assignment?.worker_classification || '';
  const personStatus = (lockedPerson ? (lockedPerson.is_active === false ? 'Inactive' : 'Active') : (assignment?.is_active === false ? 'Inactive' : 'Active'));
  const selAcct = accounts.find((a) => a.account_number === f.account_number);
  const selLoc = locations.find((l) => l.id === f.location_id) || (assignment ? { location_name: assignment.location_name, address: assignment.location_address } : null);
  const custName = customers.find((c) => c.id === f.customer_id)?.name || assignment?.customer_name || '';

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Assignment' : 'New Assignment'}</DialogTitle></DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-auto pr-1">
          {/* ---------- PERSONNEL (identity) ---------- */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="assignment-personnel-section">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Personnel {personLocked && <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">Locked</span>}</div>
            {personLocked ? (
              <div data-testid="assignment-person-readonly">
                <div className="text-base font-semibold text-[#0E2A47]">{personName || '—'}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                  <span>Personnel #: <b>{personNumber || '—'}</b></span>
                  <span>Classification: <b>{personClass || '—'}</b></span>
                  <span>Status: <b>{personStatus}</b></span>
                </div>
                <p className="mt-1.5 text-xs text-slate-400">To assign a different person, create a new assignment — the person cannot be changed here.</p>
              </div>
            ) : (
              <Field label="Personnel" required>
                <SearchableSelect testid="assignment-personnel" options={personnelOpts} value={f.personnel_id}
                  onChange={(v) => setF((p) => ({ ...p, personnel_id: v }))}
                  placeholder="Search personnel…" />
              </Field>
            )}
          </div>

          {/* ---------- ASSIGNMENT ---------- */}
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Assignment</div>
          {isEdit && (
            <p className="-mt-2 text-xs text-slate-400" data-testid="assignment-locked-hint">
              Scope, customer and service location are locked. To move this person to a different customer or location, end this assignment (set an Effective End date) and create a new one — the change is date-stamped in history. You can still correct the <b>Account</b> below.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={isEdit ? 'Assignment Scope (locked)' : 'Assignment Scope'} required>
              <select className={inputCls} value={f.assignment_scope} disabled={isEdit} data-testid="assignment-scope"
                onChange={(e) => setF((p) => ({ ...p, assignment_scope: e.target.value, location_id: '' }))}>
                <option value="service_location">Service Location</option>
                <option value="customer">Customer (account-wide)</option>
              </select>
            </Field>
            <Field label="Assignment Role" required>
              <select className={inputCls} value={f.assignment_role} data-testid="assignment-role"
                onChange={(e) => setF((p) => ({ ...p, assignment_role: e.target.value }))}>
                <option value="">Select role…</option>
                {meta.assignment_roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={isEdit ? 'Customer (locked)' : 'Customer'} required>
              <select className={inputCls} value={f.customer_id} disabled={isEdit} data-testid="assignment-customer"
                onChange={(e) => setF((p) => ({ ...p, customer_id: e.target.value, location_id: '', account_number: '' }))}>
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.company_name}</option>)}
              </select>
            </Field>
            {f.assignment_scope === 'service_location' && (
              <Field label={isEdit ? 'Service Location (locked)' : 'Service Location'} required>
                <select className={inputCls} value={f.location_id} disabled={isEdit || !f.customer_id} data-testid="assignment-location"
                  onChange={(e) => {
                    const lid = e.target.value;
                    const lacct = locations.find((l) => l.id === lid)?.account_number;
                    setF((p) => ({ ...p, location_id: lid, account_number: lacct != null ? String(lacct) : '' }));
                  }}>
                  <option value="">{f.customer_id ? 'Select location…' : 'Pick customer first'}</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.location_name}</option>)}
                </select>
              </Field>
            )}
          </div>
          <Field label="Account" required>
            {f.assignment_scope === 'service_location' ? (
              <>
                <select className={inputCls} value={f.account_number} disabled data-testid="assignment-account"
                  onChange={() => {}}>
                  <option value="">{f.location_id ? '—' : 'Pick a service location first'}</option>
                  {selLoc && (f.account_number || selLoc.account_number) && (
                    <option value={f.account_number || String(selLoc.account_number)}>
                      {(f.account_number || selLoc.account_number)} — {selLoc.location_name || (selAcct?.account_name) || ''}
                    </option>
                  )}
                </select>
                <p className="mt-1 text-xs text-slate-400" data-testid="assignment-account-derived-hint">
                  The account is set automatically from the selected service location and cannot be mismatched.
                </p>
              </>
            ) : (
              <select className={inputCls} value={f.account_number} disabled={!f.customer_id} data-testid="assignment-account"
                onChange={(e) => setF((p) => ({ ...p, account_number: e.target.value }))}>
                <option value="">{f.customer_id ? 'Select account…' : 'Pick customer first'}</option>
                {accounts.map((a) => <option key={a.account_number} value={a.account_number}>{a.account_number} — {a.account_name}</option>)}
              </select>
            )}
          </Field>
          {(selAcct || f.customer_id) && (
            <div className="rounded-lg border border-[#0E2A47]/15 bg-[#0E2A47]/[0.03] p-3 text-sm" data-testid="assignment-account-identity">
              <div className="text-base font-bold text-[#0E2A47]">{selAcct ? `${selAcct.account_number} — ${selAcct.account_name}` : '— select an account —'}</div>
              <div className="mt-1 text-slate-600">Customer: <b>{custName || '—'}</b></div>
              {f.assignment_scope === 'service_location' && <div className="text-slate-600">Service Location: <b>{selLoc?.location_name || '—'}</b>{selLoc?.address ? <span className="text-slate-400"> · {selLoc.address}</span> : null}</div>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Effective Start" required>
              <input type="date" className={inputCls} value={f.effective_start_date} data-testid="assignment-start"
                onChange={(e) => setF((p) => ({ ...p, effective_start_date: e.target.value }))} />
            </Field>
            <Field label="Effective End (optional)">
              <input type="date" className={inputCls} value={f.effective_end_date} data-testid="assignment-end-date"
                onChange={(e) => setF((p) => ({ ...p, effective_end_date: e.target.value }))} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.is_primary} data-testid="assignment-primary"
              onChange={(e) => setF((p) => ({ ...p, is_primary: e.target.checked }))} />
            Primary assignment (vs. backup coverage)
          </label>
          <Field label="Notes (optional)">
            <textarea className={inputCls} rows={2} value={f.assignment_notes} data-testid="assignment-notes"
              onChange={(e) => setF((p) => ({ ...p, assignment_notes: e.target.value }))} />
          </Field>
          {warnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="assignment-warnings">
              <div className="font-semibold">Overlap warning — review before saving (not merged automatically):</div>
              <ul className="mt-1 list-disc pl-4">
                {warnings.map((w, i) => <li key={i}>{w.assignment_role} ({w.effective_start_date ? fmtDate(w.effective_start_date) : '—'} → {w.effective_end_date ? fmtDate(w.effective_end_date) : '—'}){w.exact_duplicate ? ' — EXACT DUPLICATE (will be blocked)' : ''}</li>)}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="assignment-save"
            className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Assignment')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {reactivate && (
      <Dialog open onOpenChange={() => setReactivate(null)}>
        <DialogContent className="max-w-md" data-testid="reactivate-continue-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><ShieldAlert size={18} /> Personnel is Inactive</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p><b>{reactivate.personnel_name || 'This person'}</b> is currently Inactive and cannot receive an assignment until reactivated. Your entered assignment details are preserved.</p>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reactivation reason <span className="text-red-500">*</span></span>
              <textarea rows={2} value={reactivateReason} onChange={(e) => setReactivateReason(e.target.value)}
                data-testid="reactivate-reason" className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="Why is this person being reactivated? (recorded in the audit history)" />
            </label>
            <p className="text-xs text-slate-400">Reactivation is a deliberate, audited action. If you lack authority, you'll be told and your assignment entries will be kept.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setReactivate(null)} data-testid="reactivate-cancel"
              className="rounded-md border border-gray-200 px-4 py-2 text-sm">Cancel</button>
            <button onClick={doReactivateAndContinue} disabled={reactivating} data-testid="reactivate-continue"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#5FA718] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4e8a13] disabled:opacity-50">
              {reactivating ? <Loader2 className="animate-spin" size={15} /> : <Power size={15} />} Reactivate &amp; Continue
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

const StatusDialog = ({ target, token, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const activating = target.next === true;
  const save = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/assignments/${target.a.id}/status`,
        { is_active: target.next, status_reason: reason, effective_end_date: endDate || undefined },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(activating ? 'Assignment reactivated' : 'Assignment ended');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{activating ? 'Reactivate' : 'End / Deactivate'} Assignment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">{target.a.name} — {target.a.assignment_role} @ {target.a.customer_name}{target.a.location_name ? ` / ${target.a.location_name}` : ''}</p>
          {target.a.account_number && <p className="text-sm font-semibold text-[#0E2A47]" data-testid="status-account">Account {target.a.account_number} — {target.a.account_name || ''}</p>}
          {!activating && (
            <Field label="Effective End Date (optional)">
              <input type="date" className={inputCls} value={endDate} data-testid="status-end-date" onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          )}
          <Field label="Reason" required>
            <textarea className={inputCls} rows={2} value={reason} data-testid="status-reason" onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="status-save"
            className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Confirm'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReconcileDialog = ({ assignment, token, onClose, onSaved }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [personnelId, setPersonnelId] = useState('');
  const [resolution, setResolution] = useState('link');
  const [correctSpelling, setCorrectSpelling] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadPreview = useCallback(async (pid) => {
    try {
      const q = pid ? `?personnel_id=${pid}` : '';
      const r = await axios.get(`${API}/assignments/${assignment.id}/reconcile${q}`, H);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load reconciliation'); onClose(); }
  }, [assignment.id]);

  useEffect(() => { loadPreview(''); }, [loadPreview]);
  useEffect(() => { if (personnelId) loadPreview(personnelId); }, [personnelId]);

  useEffect(() => {
    if (data?.effect?.recommended_resolution) setResolution(data.effect.recommended_resolution);
  }, [data?.effect?.recommended_resolution]);

  const legacy = data?.legacy;
  const effect = data?.effect;
  const apply = async () => {
    if (reason.trim().length < 3) { toast.error('A reconciliation reason is required'); return; }
    if (resolution !== 'purge' && !personnelId) { toast.error('Select the matching personnel record'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/assignments/${assignment.id}/reconcile`,
        { resolution, personnel_id: personnelId || undefined, correct_spelling: correctSpelling, reason }, H);
      toast.success(resolution === 'purge' ? 'Legacy assignment purged' : resolution === 'merge' ? 'Merged into existing assignment' : 'Legacy assignment linked');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reconciliation failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="reconcile-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 size={18} /> Reconcile Legacy Record</DialogTitle></DialogHeader>
        {!data ? (
          <div className="flex items-center gap-2 py-10 text-slate-500"><Loader2 className="animate-spin" size={18} /> Loading legacy record…</div>
        ) : (
          <div className="max-h-[65vh] space-y-4 overflow-auto pr-1">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="reconcile-source">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-700">Unlinked Legacy Assignment</div>
              <div className="mt-1 text-base font-semibold text-[#0E2A47]">{legacy?.name || '—'}</div>
              <div className="mt-0.5 text-slate-600">{legacy?.customer_name}{legacy?.location_name ? ` · ${legacy.location_name}` : ''}{legacy?.assignment_role ? ` · ${legacy.assignment_role}` : ''}</div>
              {legacy?.account_number && <div className="text-xs text-slate-500">Account {legacy.account_number} — {legacy.account_name}</div>}
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Link to existing Personnel Master record</div>
              <SearchableSelect testid="reconcile-personnel" value={personnelId} onChange={setPersonnelId}
                options={(data.candidates || []).map((c) => ({ value: c.id, label: `${c.name} — #${c.personnel_number}`, sublabel: c.worker_classification }))}
                placeholder="Choose the matching person…" />
              <p className="mt-1 text-xs text-slate-400">A new personnel record is never created — you are linking to an existing one.</p>
            </div>

            {effect && (
              <div className="rounded-lg border border-[#0E2A47]/15 bg-[#0E2A47]/[0.03] p-3 text-sm" data-testid="reconcile-effect">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Preview of effect</div>
                <div className="mt-1 grid gap-1">
                  <div>Target: <b>{effect.corrected_name}</b> (#{effect.will_set_employee_identifier})</div>
                  {effect.spelling_differs && (
                    <label className="flex items-center gap-2 text-slate-700" data-testid="reconcile-spelling">
                      <input type="checkbox" checked={correctSpelling} onChange={(e) => setCorrectSpelling(e.target.checked)} />
                      Correct displayed spelling: <span className="line-through text-slate-400">{effect.current_name}</span> → <b>{effect.corrected_name}</b>
                    </label>
                  )}
                  {effect.duplicate && (
                    <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="reconcile-duplicate">
                      A matching active assignment already exists for this person — merging will keep it and remove this legacy duplicate, preserving history.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Resolution</div>
              <div className="space-y-1.5 text-sm">
                <label className="flex items-center gap-2"><input type="radio" name="res" checked={resolution === 'link'} onChange={() => setResolution('link')} data-testid="reconcile-res-link" /> Link to this person (preserve assignment history)</label>
                {effect?.duplicate && <label className="flex items-center gap-2"><input type="radio" name="res" checked={resolution === 'merge'} onChange={() => setResolution('merge')} data-testid="reconcile-res-merge" /> Merge into the existing assignment (remove duplicate)</label>}
                <label className="flex items-center gap-2 text-red-700"><input type="radio" name="res" checked={resolution === 'purge'} onChange={() => setResolution('purge')} data-testid="reconcile-res-purge" /> Purge — this orphaned legacy assignment is invalid</label>
              </div>
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reason <span className="text-red-500">*</span></span>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reconcile-reason"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" placeholder="Recorded in the reconciliation audit history" />
            </label>
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm" data-testid="reconcile-cancel">Cancel</button>
          <button onClick={apply} disabled={busy || !data} data-testid="reconcile-apply"
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${resolution === 'purge' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0E2A47] hover:bg-[#16324F]'}`}>
            {busy ? <Loader2 className="animate-spin" size={15} /> : null}
            {resolution === 'purge' ? 'Purge Legacy Record' : resolution === 'merge' ? 'Merge & Remove Duplicate' : 'Link Record'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BASIS_OPTS = [
  { v: 'monthly_location', l: 'Monthly Location Amount' },
  { v: 'hourly', l: 'Hourly' }, { v: 'daily', l: 'Daily' },
  { v: 'weekly_flat', l: 'Weekly-Flat (fixed weekly amount)' },
  { v: 'salary', l: 'Salaried (flat per period)' }, { v: 'flat_unit', l: 'Flat / Unit-based' },
];
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const _amt = (o) => {
  if (!o) return '—';
  const v = o.approved_monthly_payout ?? o.weekly_reference_amount ?? o.rate ?? o.amount;
  return v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';
};

const CompApprovalQueue = ({ token, owner, onClose, onDecided }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [action, setAction] = useState({}); // { [reqId]: { kind, comment } }

  const load = useCallback(() => {
    axios.get(`${API}/assignments/compensation-requests/queue`, H)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => { toast.error('Could not load the approval queue'); setItems([]); });
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const decide = async (req, decision) => {
    const st = action[req.id] || {};
    const comment = (st.comment || '').trim();
    if ((decision === 'reject' || decision === 'return') && !comment) {
      toast.error('A reason is required to reject or return'); return;
    }
    setBusyId(req.id);
    try {
      const r = await axios.post(`${API}/assignments/compensation-request/${req.id}/decision`,
        { decision, comments: comment }, H);
      const res = r.data?.compensation_request || {};
      toast.success(`Compensation ${decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'returned'}`);
      if (res.affects_past_period) toast.warning('Effective date is in the past — flag for authorized payroll review.');
      setAction((p) => { const n = { ...p }; delete n[req.id]; return n; });
      load(); onDecided?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Decision failed'); }
    setBusyId(null);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="comp-queue-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign size={18} /> Compensation Approval Queue</DialogTitle>
        </DialogHeader>
        {!owner && (
          <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="comp-queue-readonly">
            View-only. Only a Global Administrator can approve, reject or return compensation changes.
          </div>
        )}
        {items === null ? (
          <div className="flex h-32 items-center justify-center text-slate-400"><Loader2 className="mr-2 animate-spin" size={18} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="comp-queue-empty">No pending compensation requests.</div>
        ) : (
          <div className="space-y-3">
            {items.map((req) => {
              const st = action[req.id] || {};
              return (
                <div key={req.id} className="rounded-xl border border-slate-200 p-4" data-testid={`comp-queue-item-${req.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold text-[#0E2A47]">{req.personnel_name} <span className="text-xs font-normal text-slate-400">#{req.personnel_number}</span></div>
                      <div className="text-xs text-slate-500">{req.service_location || req.customer} · {req.assignment_role || '—'}</div>
                    </div>
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{req.change_type}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div className="rounded-lg bg-slate-50 p-2.5">
                      <div className="font-semibold uppercase tracking-wide text-slate-400">Current</div>
                      <div className="mt-0.5 text-slate-700">{_amt(req.current)} {(req.current?.frequency_label || req.current?.frequency) ? `· ${req.current.frequency_label || req.current.frequency}` : ''}</div>
                    </div>
                    <div className="rounded-lg bg-emerald-50 p-2.5">
                      <div className="font-semibold uppercase tracking-wide text-emerald-600">Proposed</div>
                      <div className="mt-0.5 text-slate-700">{_amt(req.proposed)} {req.proposed?.frequency_label ? `· ${req.proposed.frequency_label}` : ''}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Effective <span className="font-semibold text-slate-700">{fmtDate(req.requested_effective_date)}</span> · Requested by {req.initiated_by}
                  </div>
                  <div className="mt-1 text-xs text-slate-600"><span className="font-semibold">Reason:</span> {req.reason}</div>
                  {st.kind && (
                    <textarea value={st.comment || ''} onChange={(e) => setAction((p) => ({ ...p, [req.id]: { ...st, comment: e.target.value } }))}
                      data-testid={`comp-queue-comment-${req.id}`} rows={2}
                      placeholder={st.kind === 'reject' ? 'Reason for rejection (required)' : 'What needs correcting (required)'}
                      className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                  )}
                  {owner && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {!st.kind && (
                        <button onClick={() => decide(req, 'approve')} disabled={busyId === req.id} data-testid={`comp-queue-approve-${req.id}`}
                          className="rounded bg-[#5FA718] px-3 py-1 text-xs font-semibold text-white hover:bg-[#4e8f12] disabled:opacity-60">Approve</button>
                      )}
                      {st.kind === 'reject' ? (
                        <button onClick={() => decide(req, 'reject')} disabled={busyId === req.id} data-testid={`comp-queue-confirm-reject-${req.id}`}
                          className="rounded bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60">Confirm Reject</button>
                      ) : !st.kind && (
                        <button onClick={() => setAction((p) => ({ ...p, [req.id]: { kind: 'reject', comment: '' } }))} data-testid={`comp-queue-reject-${req.id}`}
                          className="rounded bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700">Reject</button>
                      )}
                      {st.kind === 'return' ? (
                        <button onClick={() => decide(req, 'return')} disabled={busyId === req.id} data-testid={`comp-queue-confirm-return-${req.id}`}
                          className="rounded border border-amber-400 px-3 py-1 text-xs font-semibold text-amber-700">Confirm Return</button>
                      ) : !st.kind && (
                        <button onClick={() => setAction((p) => ({ ...p, [req.id]: { kind: 'return', comment: '' } }))} data-testid={`comp-queue-return-${req.id}`}
                          className="rounded border border-amber-400 px-3 py-1 text-xs font-semibold text-amber-700">Return</button>
                      )}
                      {st.kind && (
                        <button onClick={() => setAction((p) => { const n = { ...p }; delete n[req.id]; return n; })} data-testid={`comp-queue-cancel-${req.id}`}
                          className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600">Cancel</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CompRequestForm = ({ assignment, token, onCancel, onDone }) => {
  const [f, setF] = useState({
    rate_basis: 'monthly_location', cadence: 'monthly',
    approved_monthly_payout: '', rate: '', weekly_reference_amount: '', scheduled_days_per_week: '',
    weekly_schedule: ['mon', 'tue', 'wed', 'thu', 'fri'],
    frequency_label: 'Monthly', requested_effective_date: '', reason: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleDay = (d) => setF((s) => ({ ...s, weekly_schedule: s.weekly_schedule.includes(d) ? s.weekly_schedule.filter((x) => x !== d) : [...s.weekly_schedule, d] }));
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        rate_basis: f.rate_basis, cadence: f.cadence, frequency_label: f.frequency_label,
        requested_effective_date: f.requested_effective_date, reason: f.reason, notes: f.notes,
      };
      if (f.rate_basis === 'monthly_location') { body.approved_monthly_payout = parseFloat(f.approved_monthly_payout); body.weekly_schedule = f.weekly_schedule; }
      else if (f.rate_basis === 'weekly_flat') { body.weekly_reference_amount = parseFloat(f.weekly_reference_amount); body.scheduled_days_per_week = parseInt(f.scheduled_days_per_week, 10); }
      else { body.rate = parseFloat(f.rate); }
      await axios.post(`${API}/assignments/${assignment.id}/compensation-request`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Compensation change submitted for Global Administrator approval');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit the request'); }
    setBusy(false);
  };
  const Lbl = ({ children }) => <label className="mb-0.5 block text-[11px] font-semibold text-slate-500">{children}</label>;
  const cur = assignment.compensation;
  return (
    <div className="space-y-2.5 rounded-lg border border-slate-200 p-3" data-testid="assignment-comp-request-form">
      {cur && <div className="text-[11px] text-slate-500">Current: <b>{cur.frequency}</b> · {cur.amount != null ? `$${Number(cur.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'} ({cur.rate_basis_label})</div>}
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Proposed pay basis</Lbl>
          <select value={f.rate_basis} onChange={(e) => { const v = e.target.value; set('rate_basis', v); if (v === 'monthly_location') set('cadence', 'monthly'); }} data-testid="comp-req-basis" className="w-full rounded border border-slate-300 px-2 py-1 text-sm">
            {BASIS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div><Lbl>Pay frequency (payroll group)</Lbl>
          <select value={f.cadence} onChange={(e) => { set('cadence', e.target.value); set('frequency_label', e.target.value === 'monthly' ? 'Monthly' : 'Semi-Monthly'); }} data-testid="comp-req-cadence" className="w-full rounded border border-slate-300 px-2 py-1 text-sm">
            <option value="monthly">Monthly</option>
            <option value="first_half">Semi-Monthly — First Half</option>
            <option value="second_half">Semi-Monthly — Second Half</option>
          </select>
        </div>
      </div>
      {f.rate_basis === 'monthly_location' ? (
        <div>
          <Lbl>Approved monthly amount ($)</Lbl>
          <input type="number" value={f.approved_monthly_payout} onChange={(e) => set('approved_monthly_payout', e.target.value)} data-testid="comp-req-amount" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          <div className="mt-1 flex flex-wrap gap-1">
            {DAYS.map((d) => <button key={d} type="button" onClick={() => toggleDay(d)} className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${f.weekly_schedule.includes(d) ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-500'}`}>{d}</button>)}
          </div>
        </div>
      ) : f.rate_basis === 'weekly_flat' ? (
        <div className="grid grid-cols-2 gap-2">
          <div><Lbl>Weekly amount ($)</Lbl><input type="number" value={f.weekly_reference_amount} onChange={(e) => set('weekly_reference_amount', e.target.value)} data-testid="comp-req-amount" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" /></div>
          <div><Lbl>Days / week</Lbl><input type="number" value={f.scheduled_days_per_week} onChange={(e) => set('scheduled_days_per_week', e.target.value)} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" /></div>
        </div>
      ) : (
        <div><Lbl>Rate ($)</Lbl><input type="number" value={f.rate} onChange={(e) => set('rate', e.target.value)} data-testid="comp-req-amount" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" /></div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Requested effective date</Lbl><input type="date" value={f.requested_effective_date} onChange={(e) => set('requested_effective_date', e.target.value)} data-testid="comp-req-effective" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" /></div>
        <div><Lbl>Reason (required)</Lbl><input value={f.reason} onChange={(e) => set('reason', e.target.value)} data-testid="comp-req-reason" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" /></div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="rounded px-3 py-1.5 text-xs font-semibold text-slate-500">Cancel</button>
        <button onClick={submit} disabled={busy || !f.reason || !f.requested_effective_date} data-testid="comp-req-submit" className="rounded bg-[#0E2A47] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#15375e] disabled:opacity-60">Submit for approval</button>
      </div>
    </div>
  );
};

export default AssignmentManager;